/**
 * Parses stored coveragesAndLimits XML from an ACORD 24 master certificate
 * back into Acord24Form prefill state.
 */

import type { StoredCoverage, StoredPolicy } from './parseAcord25StoredCoverages';
export type { StoredCoverage, StoredPolicy };

export interface Acord24EditPrefill {
  // Insurers A–F (names and carrier databaseIds extracted from Property XML block)
  insurerA: string; insurerB: string; insurerC: string;
  insurerD: string; insurerE: string; insurerF: string;
  insurerAId: string | null; insurerBId: string | null; insurerCId: string | null;
  insurerDId: string | null; insurerEId: string | null; insurerFId: string | null;
  // Shared header
  certNumber: string;
  revisionNumber: string;
  certDate: Date | null;
  locationProperty: string;
  descriptionOfProperty: string;
  specialConditions: string;
  // Section-enable flags (mirrors form's propChecked / inlandChecked etc.)
  propChecked: boolean;
  inlandChecked: boolean;
  crimeChecked: boolean;
  boilerChecked: boolean;
  // Property
  propInsurerLtr: string;
  propPolicyNumber: string;
  propEffDate: Date | null;
  propExpDate: Date | null;
  propBasic: boolean;
  propBroad: boolean;
  propSpecial: boolean;
  propEarthquake: boolean;
  propWind: boolean;
  propFlood: boolean;
  propDeductBuilding: string;
  propDeductContents: string;
  propEarthquakeDeduct: string;
  propWindDeduct: string;
  propFloodDeduct: string;
  propOther1: boolean;
  propOther1Text: string;
  propOther1Deduct: string;
  propOther2: boolean;
  propOther2Text: string;
  propOther2Deduct: string;
  propCovBuilding: boolean;
  propLimBuilding: string;
  propCovPersonalProp: boolean;
  propLimPersonalProp: string;
  propCovBusinessIncome: boolean;
  propLimBusinessIncome: string;
  propCovExtraExpense: boolean;
  propLimExtraExpense: string;
  propCovRentalValue: boolean;
  propLimRentalValue: string;
  propCovBlanketBuilding: boolean;
  propLimBlanketBuilding: string;
  propCovBlanketPersProp: boolean;
  propLimBlanketPersProp: string;
  propCovBlanketBldgPP: boolean;
  propLimBlanketBldgPP: string;
  propOtherCov1: boolean;
  propOtherCov1Desc: string;
  propOtherCov1Lim: string;
  propOtherCov2: boolean;
  propOtherCov2Desc: string;
  propOtherCov2Lim: string;
  // Inland Marine
  inlandInsurerLtr: string;
  inlandPolicyNumber: string;
  inlandEffDate: Date | null;
  inlandExpDate: Date | null;
  inlandNamedPerils: boolean;
  inlandOtherPerils: boolean;
  inlandOtherPerilsText: string;
  inlandTypeOfPolicy: string;
  inlandCov1: boolean; inlandCov1Desc: string; inlandCov1Lim: string;
  inlandCov2: boolean; inlandCov2Desc: string; inlandCov2Lim: string;
  inlandCov3: boolean; inlandCov3Desc: string; inlandCov3Lim: string;
  inlandCov4: boolean; inlandCov4Desc: string; inlandCov4Lim: string;
  // Crime
  crimeInsurerLtr: string;
  crimePolicyNumber: string;
  crimeEffDate: Date | null;
  crimeExpDate: Date | null;
  crimeTypeOfPolicy: string;
  crimeCov1: boolean; crimeCov1Desc: string; crimeCov1Lim: string;
  crimeCov2: boolean; crimeCov2Desc: string; crimeCov2Lim: string;
  crimeCov3: boolean; crimeCov3Desc: string; crimeCov3Lim: string;
  // Boiler & Machinery
  boilerInsurerLtr: string;
  boilerPolicyNumber: string;
  boilerEffDate: Date | null;
  boilerExpDate: Date | null;
  boilerCov1: boolean; boilerCov1Desc: string; boilerCov1Lim: string;
  boilerCov2: boolean; boilerCov2Desc: string; boilerCov2Lim: string;
  // Other
  otherInsurerLtr: string;
  otherType: string;
  otherPolicyNumber: string;
  otherEffDate: Date | null;
  otherExpDate: Date | null;
  otherCov1: boolean; otherCov1Desc: string; otherCov1Lim: string;
  otherCov2: boolean; otherCov2Desc: string; otherCov2Lim: string;
  // Misc
  acord101AdditionalRemarks: string;
  authRepSignature: string;
  contactName: string; contactPhone: string; contactFax: string; contactEmail: string; producerCustomerId: string;
  // Certificate holder
  holderName: string;
  holderAddress: string;
  holderCity: string;
  holderStreet: string;
  holderState: string;
  holderZip: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseDoc(xml: string): Document {
  return new DOMParser().parseFromString(`<root>${xml}</root>`, 'text/xml');
}

function getText(doc: Document, selector: string): string {
  return (doc.querySelector(selector)?.textContent?.trim() || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}


function yn(v: string): boolean {
  return ['yes', 'true', '1'].includes(v.trim().toLowerCase());
}

function parseStoredDate(v: string | null | undefined): Date | null {
  if (!v) return null;
  const slash = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) return new Date(Number(slash[3]), Number(slash[1]) - 1, Number(slash[2]));
  const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function parseLimitEl(doc: Document, selector: string): { checked: boolean; value: string; desc: string } {
  const el = doc.querySelector(selector);
  return {
    checked: yn(el?.getAttribute('text') || ''),
    value:   el?.getAttribute('value') || el?.textContent?.trim() || '',
    desc:    el?.textContent?.trim() || '',
  };
}

// ── Main export ───────────────────────────────────────────────────────────────

export function parseAcord24StoredCoverages(
  coverages: StoredCoverage[],
  policies: StoredPolicy[],
): Acord24EditPrefill {
  const result: Acord24EditPrefill = {
    insurerA: '', insurerB: '', insurerC: '', insurerD: '', insurerE: '', insurerF: '',
    insurerAId: null, insurerBId: null, insurerCId: null, insurerDId: null, insurerEId: null, insurerFId: null,
    certNumber: '', revisionNumber: '',
    certDate: null, locationProperty: '', descriptionOfProperty: '', specialConditions: '',
    propChecked: false, inlandChecked: false, crimeChecked: false, boilerChecked: false,
    propInsurerLtr: '', propPolicyNumber: '', propEffDate: null, propExpDate: null,
    propBasic: false, propBroad: false, propSpecial: false,
    propEarthquake: false, propWind: false, propFlood: false,
    propDeductBuilding: '', propDeductContents: '',
    propEarthquakeDeduct: '', propWindDeduct: '', propFloodDeduct: '',
    propOther1: false, propOther1Text: '', propOther1Deduct: '',
    propOther2: false, propOther2Text: '', propOther2Deduct: '',
    propCovBuilding: false, propLimBuilding: '',
    propCovPersonalProp: false, propLimPersonalProp: '',
    propCovBusinessIncome: false, propLimBusinessIncome: '',
    propCovExtraExpense: false, propLimExtraExpense: '',
    propCovRentalValue: false, propLimRentalValue: '',
    propCovBlanketBuilding: false, propLimBlanketBuilding: '',
    propCovBlanketPersProp: false, propLimBlanketPersProp: '',
    propCovBlanketBldgPP: false, propLimBlanketBldgPP: '',
    propOtherCov1: false, propOtherCov1Desc: '', propOtherCov1Lim: '',
    propOtherCov2: false, propOtherCov2Desc: '', propOtherCov2Lim: '',
    inlandInsurerLtr: '', inlandPolicyNumber: '', inlandEffDate: null, inlandExpDate: null,
    inlandNamedPerils: false, inlandOtherPerils: false, inlandOtherPerilsText: '', inlandTypeOfPolicy: '',
    inlandCov1: false, inlandCov1Desc: '', inlandCov1Lim: '',
    inlandCov2: false, inlandCov2Desc: '', inlandCov2Lim: '',
    inlandCov3: false, inlandCov3Desc: '', inlandCov3Lim: '',
    inlandCov4: false, inlandCov4Desc: '', inlandCov4Lim: '',
    crimeInsurerLtr: '', crimePolicyNumber: '', crimeEffDate: null, crimeExpDate: null,
    crimeTypeOfPolicy: '',
    crimeCov1: false, crimeCov1Desc: '', crimeCov1Lim: '',
    crimeCov2: false, crimeCov2Desc: '', crimeCov2Lim: '',
    crimeCov3: false, crimeCov3Desc: '', crimeCov3Lim: '',
    boilerInsurerLtr: '', boilerPolicyNumber: '', boilerEffDate: null, boilerExpDate: null,
    boilerCov1: false, boilerCov1Desc: '', boilerCov1Lim: '',
    boilerCov2: false, boilerCov2Desc: '', boilerCov2Lim: '',
    otherInsurerLtr: '', otherType: '', otherPolicyNumber: '',
    otherEffDate: null, otherExpDate: null,
    otherCov1: false, otherCov1Desc: '', otherCov1Lim: '',
    otherCov2: false, otherCov2Desc: '', otherCov2Lim: '',
    acord101AdditionalRemarks: '', authRepSignature: '',
    contactName: '', contactPhone: '', contactFax: '', contactEmail: '', producerCustomerId: '',
    holderName: '', holderAddress: '', holderCity: '', holderStreet: '', holderState: '', holderZip: '',
  };

  // Build policyNumber → carrierName lookup
  const policyCarrierMap: Record<string, string> = {};
  for (const p of policies) {
    if (p.number && p.carrierName) policyCarrierMap[p.number] = p.carrierName;
  }

  const carrierToLetter: Record<string, string> = {};
  const letters = ['A', 'B', 'C', 'D', 'E', 'F'] as const;
  let nextLetterIdx = 0;
  const insurerSlots: Record<string, string> = {};

  // Assigns an insurer letter for a coverage, keyed by carrier name (preferred) or naiCid (fallback for legacy data).
  const assignInsurer = (policyNumber: string, naiCid?: string | null): string => {
    const carrier = policyCarrierMap[policyNumber] || '';
    const key = carrier || naiCid || '';
    if (!key) return '';
    if (carrierToLetter[key]) return carrierToLetter[key];
    if (nextLetterIdx >= 6) return '';
    const letter = letters[nextLetterIdx++];
    carrierToLetter[key] = letter;
    if (carrier) insurerSlots[letter] = carrier;
    return letter;
  };

  /** Store naiCid for the given insurer letter slot (first value wins). */
  const setSlotId = (letter: string, naiCid: string | null) => {
    if (!naiCid) return;
    if (letter === 'A' && !result.insurerAId) result.insurerAId = naiCid;
    else if (letter === 'B' && !result.insurerBId) result.insurerBId = naiCid;
    else if (letter === 'C' && !result.insurerCId) result.insurerCId = naiCid;
    else if (letter === 'D' && !result.insurerDId) result.insurerDId = naiCid;
    else if (letter === 'E' && !result.insurerEId) result.insurerEId = naiCid;
    else if (letter === 'F' && !result.insurerFId) result.insurerFId = naiCid;
  };

  for (const cov of coverages) {
    if (cov.signatureId && !result.authRepSignature) result.authRepSignature = cov.signatureId;
    if (!cov.coveragesAndLimits) continue;

    if (cov.acordFormNumber === 101) {
      const doc = parseDoc(cov.coveragesAndLimits);
      result.acord101AdditionalRemarks = getText(doc, 'Acord101AdditionalRemarks RemarksText');
      continue;
    }

    const doc = parseDoc(cov.coveragesAndLimits);
    const hasCov = (tag: string) => !!doc.querySelector(`Coverages ${tag}`);

    if (hasCov('Acord24PropertyLiability')) {
      result.propChecked = yn(getText(doc, 'Coverages Acord24PropertyLiability Property'));
      const xmlLtr = getText(doc, 'Coverages Acord24PropertyLiability InsurerLtr');
      const letter = xmlLtr || assignInsurer(cov.policyNumber, cov.naiCid);
      setSlotId(letter, cov.naiCid);
      result.propInsurerLtr  = letter;
      result.propPolicyNumber = cov.policyNumber || '';
      result.propEffDate      = parseStoredDate(cov.effectiveDate);
      result.propExpDate      = parseStoredDate(cov.expirationDate);

      // Insurer names stored inside Property XML
      result.insurerA = getText(doc, 'Acord24PropertyLiability InsurerA') || insurerSlots['A'] || '';
      result.insurerB = getText(doc, 'Acord24PropertyLiability InsurerB') || insurerSlots['B'] || '';
      result.insurerC = getText(doc, 'Acord24PropertyLiability InsurerC') || insurerSlots['C'] || '';
      result.insurerD = getText(doc, 'Acord24PropertyLiability InsurerD') || insurerSlots['D'] || '';
      result.insurerE = getText(doc, 'Acord24PropertyLiability InsurerE') || insurerSlots['E'] || '';
      result.insurerF = getText(doc, 'Acord24PropertyLiability InsurerF') || insurerSlots['F'] || '';
      // Insurer carrier databaseIds (stored since NaicId fix) — only override if the XML has a non-empty value
      const xmlAId = getText(doc, 'Acord24PropertyLiability InsurerAId');
      const xmlBId = getText(doc, 'Acord24PropertyLiability InsurerBId');
      const xmlCId = getText(doc, 'Acord24PropertyLiability InsurerCId');
      const xmlDId = getText(doc, 'Acord24PropertyLiability InsurerDId');
      const xmlEId = getText(doc, 'Acord24PropertyLiability InsurerEId');
      const xmlFId = getText(doc, 'Acord24PropertyLiability InsurerFId');
      if (xmlAId) result.insurerAId = xmlAId;
      if (xmlBId) result.insurerBId = xmlBId;
      if (xmlCId) result.insurerCId = xmlCId;
      if (xmlDId) result.insurerDId = xmlDId;
      if (xmlEId) result.insurerEId = xmlEId;
      if (xmlFId) result.insurerFId = xmlFId;

      const dateStr = getText(doc, 'Coverages Acord24PropertyLiability Date');
      if (dateStr) result.certDate = parseStoredDate(dateStr);
      result.certNumber     = getText(doc, 'Coverages Acord24PropertyLiability CertificateNumber');
      result.revisionNumber = getText(doc, 'Coverages Acord24PropertyLiability RevisionNumber');
      result.contactName     = getText(doc, 'Coverages Acord24PropertyLiability ContactName');
      result.contactPhone    = getText(doc, 'Coverages Acord24PropertyLiability Phone');
      result.contactFax      = getText(doc, 'Coverages Acord24PropertyLiability Fax');
      result.contactEmail    = getText(doc, 'Coverages Acord24PropertyLiability Email');
      result.producerCustomerId = getText(doc, 'Coverages Acord24PropertyLiability CustomerId');
      const propLocation = getText(doc, 'Coverages Acord24PropertyLiability LocationOfPremises');
      const propDescription = getText(doc, 'Coverages Acord24PropertyLiability DescriptionOfProperty');
      const propSpecial = getText(doc, 'Coverages Acord24PropertyLiability SpecialConditions');
      if (propLocation) result.locationProperty = propLocation;
      if (propDescription) result.descriptionOfProperty = propDescription;
      if (propSpecial) result.specialConditions = propSpecial;

      // Cause-of-loss checkboxes: Basic has deductBuilding in text attr
      const basicEl = doc.querySelector('Coverages Acord24PropertyLiability Basic');
      result.propBasic         = yn(basicEl?.textContent || '');
      result.propDeductBuilding = basicEl?.getAttribute('text') || '';
      const specialEl = doc.querySelector('Coverages Acord24PropertyLiability Special');
      result.propSpecial        = yn(specialEl?.textContent || '');
      result.propDeductContents = specialEl?.getAttribute('text') || '';
      result.propBroad      = yn(getText(doc, 'Coverages Acord24PropertyLiability Broad'));
      const eqEl = doc.querySelector('Coverages Acord24PropertyLiability EarthQuake');
      result.propEarthquake      = yn(eqEl?.textContent || '');
      result.propEarthquakeDeduct = eqEl?.getAttribute('text') || '';
      const windEl = doc.querySelector('Coverages Acord24PropertyLiability Wind');
      result.propWind      = yn(windEl?.textContent || '');
      result.propWindDeduct = windEl?.getAttribute('text') || '';
      const floodEl = doc.querySelector('Coverages Acord24PropertyLiability Flood');
      result.propFlood      = yn(floodEl?.textContent || '');
      result.propFloodDeduct = floodEl?.getAttribute('text') || '';
      const o1El = doc.querySelector('Coverages Acord24PropertyLiability Other1');
      result.propOther1      = yn(o1El?.getAttribute('text') || '');
      result.propOther1Deduct = o1El?.getAttribute('value') || '';
      result.propOther1Text   = o1El?.textContent?.trim() || '';
      const o2El = doc.querySelector('Coverages Acord24PropertyLiability Other2');
      result.propOther2      = yn(o2El?.getAttribute('text') || '');
      result.propOther2Deduct = o2El?.getAttribute('value') || '';
      result.propOther2Text   = o2El?.textContent?.trim() || '';

      // Limits
      const bldg = parseLimitEl(doc, 'Limits Acord24PropertyLiability Building');
      result.propCovBuilding = bldg.checked; result.propLimBuilding = bldg.value;
      const pp = parseLimitEl(doc, 'Limits Acord24PropertyLiability PersonalProperty');
      result.propCovPersonalProp = pp.checked; result.propLimPersonalProp = pp.value;
      const bi = parseLimitEl(doc, 'Limits Acord24PropertyLiability BusinessIncome');
      result.propCovBusinessIncome = bi.checked; result.propLimBusinessIncome = bi.value;
      const ee = parseLimitEl(doc, 'Limits Acord24PropertyLiability ExtraExpense');
      result.propCovExtraExpense = ee.checked; result.propLimExtraExpense = ee.value;
      const rv = parseLimitEl(doc, 'Limits Acord24PropertyLiability RentalValue');
      result.propCovRentalValue = rv.checked; result.propLimRentalValue = rv.value;
      const bb = parseLimitEl(doc, 'Limits Acord24PropertyLiability BlanketBuilding');
      result.propCovBlanketBuilding = bb.checked; result.propLimBlanketBuilding = bb.value;
      const bpp = parseLimitEl(doc, 'Limits Acord24PropertyLiability BlanketPersProp');
      result.propCovBlanketPersProp = bpp.checked; result.propLimBlanketPersProp = bpp.value;
      const bldgPP = parseLimitEl(doc, 'Limits Acord24PropertyLiability BlanketBldgPp');
      result.propCovBlanketBldgPP = bldgPP.checked; result.propLimBlanketBldgPP = bldgPP.value;
      const limO1 = parseLimitEl(doc, 'Limits Acord24PropertyLiability Other1');
      result.propOtherCov1 = limO1.checked; result.propOtherCov1Lim = limO1.value; result.propOtherCov1Desc = limO1.desc;
      const limO2 = parseLimitEl(doc, 'Limits Acord24PropertyLiability Other2');
      result.propOtherCov2 = limO2.checked; result.propOtherCov2Lim = limO2.value; result.propOtherCov2Desc = limO2.desc;

    } else if (hasCov('Acord24InlandMarineLiability')) {
      result.inlandChecked = yn(getText(doc, 'Coverages Acord24InlandMarineLiability InlandMarine'));
      const xmlLtr = getText(doc, 'Coverages Acord24InlandMarineLiability InsurerLtr');
      const letter = xmlLtr || assignInsurer(cov.policyNumber, cov.naiCid);
      setSlotId(letter, cov.naiCid);
      result.inlandInsurerLtr  = letter;
      result.inlandPolicyNumber = cov.policyNumber || '';
      result.inlandEffDate      = parseStoredDate(cov.effectiveDate);
      result.inlandExpDate      = parseStoredDate(cov.expirationDate);

      // Read insurer names/IDs from this section XML (present in data saved after fix)
      const inlA = getText(doc, 'Acord24InlandMarineLiability InsurerA');
      const inlB = getText(doc, 'Acord24InlandMarineLiability InsurerB');
      const inlC = getText(doc, 'Acord24InlandMarineLiability InsurerC');
      const inlD = getText(doc, 'Acord24InlandMarineLiability InsurerD');
      const inlE = getText(doc, 'Acord24InlandMarineLiability InsurerE');
      const inlF = getText(doc, 'Acord24InlandMarineLiability InsurerF');
      if (inlA && !result.insurerA) result.insurerA = inlA;
      if (inlB && !result.insurerB) result.insurerB = inlB;
      if (inlC && !result.insurerC) result.insurerC = inlC;
      if (inlD && !result.insurerD) result.insurerD = inlD;
      if (inlE && !result.insurerE) result.insurerE = inlE;
      if (inlF && !result.insurerF) result.insurerF = inlF;
      const inlAId = getText(doc, 'Acord24InlandMarineLiability InsurerAId');
      const inlBId = getText(doc, 'Acord24InlandMarineLiability InsurerBId');
      const inlCId = getText(doc, 'Acord24InlandMarineLiability InsurerCId');
      const inlDId = getText(doc, 'Acord24InlandMarineLiability InsurerDId');
      const inlEId = getText(doc, 'Acord24InlandMarineLiability InsurerEId');
      const inlFId = getText(doc, 'Acord24InlandMarineLiability InsurerFId');
      if (inlAId && !result.insurerAId) result.insurerAId = inlAId;
      if (inlBId && !result.insurerBId) result.insurerBId = inlBId;
      if (inlCId && !result.insurerCId) result.insurerCId = inlCId;
      if (inlDId && !result.insurerDId) result.insurerDId = inlDId;
      if (inlEId && !result.insurerEId) result.insurerEId = inlEId;
      if (inlFId && !result.insurerFId) result.insurerFId = inlFId;

      if (!result.locationProperty)
        result.locationProperty = getText(doc, 'Coverages Acord24InlandMarineLiability LocationOfPremises');
      if (!result.descriptionOfProperty)
        result.descriptionOfProperty = getText(doc, 'Coverages Acord24InlandMarineLiability DescriptionOfProperty');
      if (!result.specialConditions)
        result.specialConditions = getText(doc, 'Coverages Acord24InlandMarineLiability SpecialConditions');

      result.inlandNamedPerils    = yn(getText(doc, 'Coverages Acord24InlandMarineLiability NamedPerils'));
      const otherEl = doc.querySelector('Coverages Acord24InlandMarineLiability Other');
      result.inlandOtherPerils    = yn(otherEl?.textContent || '');
      result.inlandOtherPerilsText = otherEl?.getAttribute('text') || '';
      result.inlandTypeOfPolicy   = getText(doc, 'Coverages Acord24InlandMarineLiability TypeOfPolicy');

      const il1 = parseLimitEl(doc, 'Limits Acord24InlandMarineLiability Other1');
      result.inlandCov1 = il1.checked; result.inlandCov1Lim = il1.value; result.inlandCov1Desc = il1.desc;
      const il2 = parseLimitEl(doc, 'Limits Acord24InlandMarineLiability Other2');
      result.inlandCov2 = il2.checked; result.inlandCov2Lim = il2.value; result.inlandCov2Desc = il2.desc;
      const il3 = parseLimitEl(doc, 'Limits Acord24InlandMarineLiability Other3');
      result.inlandCov3 = il3.checked; result.inlandCov3Lim = il3.value; result.inlandCov3Desc = il3.desc;
      const il4 = parseLimitEl(doc, 'Limits Acord24InlandMarineLiability Other4');
      result.inlandCov4 = il4.checked; result.inlandCov4Lim = il4.value; result.inlandCov4Desc = il4.desc;

    } else if (hasCov('Acord24CrimeLiability')) {
      result.crimeChecked = yn(getText(doc, 'Coverages Acord24CrimeLiability Crime'));
      const xmlLtr = getText(doc, 'Coverages Acord24CrimeLiability InsurerLtr');
      const letter = xmlLtr || assignInsurer(cov.policyNumber, cov.naiCid);
      setSlotId(letter, cov.naiCid);
      result.crimeInsurerLtr  = letter;
      result.crimePolicyNumber = cov.policyNumber || '';
      result.crimeEffDate      = parseStoredDate(cov.effectiveDate);
      result.crimeExpDate      = parseStoredDate(cov.expirationDate);

      const crA = getText(doc, 'Acord24CrimeLiability InsurerA');
      const crB = getText(doc, 'Acord24CrimeLiability InsurerB');
      const crC = getText(doc, 'Acord24CrimeLiability InsurerC');
      const crD = getText(doc, 'Acord24CrimeLiability InsurerD');
      const crE = getText(doc, 'Acord24CrimeLiability InsurerE');
      const crF = getText(doc, 'Acord24CrimeLiability InsurerF');
      if (crA && !result.insurerA) result.insurerA = crA;
      if (crB && !result.insurerB) result.insurerB = crB;
      if (crC && !result.insurerC) result.insurerC = crC;
      if (crD && !result.insurerD) result.insurerD = crD;
      if (crE && !result.insurerE) result.insurerE = crE;
      if (crF && !result.insurerF) result.insurerF = crF;
      const crAId = getText(doc, 'Acord24CrimeLiability InsurerAId');
      const crBId = getText(doc, 'Acord24CrimeLiability InsurerBId');
      const crCId = getText(doc, 'Acord24CrimeLiability InsurerCId');
      const crDId = getText(doc, 'Acord24CrimeLiability InsurerDId');
      const crEId = getText(doc, 'Acord24CrimeLiability InsurerEId');
      const crFId = getText(doc, 'Acord24CrimeLiability InsurerFId');
      if (crAId && !result.insurerAId) result.insurerAId = crAId;
      if (crBId && !result.insurerBId) result.insurerBId = crBId;
      if (crCId && !result.insurerCId) result.insurerCId = crCId;
      if (crDId && !result.insurerDId) result.insurerDId = crDId;
      if (crEId && !result.insurerEId) result.insurerEId = crEId;
      if (crFId && !result.insurerFId) result.insurerFId = crFId;

      if (!result.locationProperty)
        result.locationProperty = getText(doc, 'Coverages Acord24CrimeLiability LocationOfPremises');
      if (!result.descriptionOfProperty)
        result.descriptionOfProperty = getText(doc, 'Coverages Acord24CrimeLiability DescriptionOfProperty');
      if (!result.specialConditions)
        result.specialConditions = getText(doc, 'Coverages Acord24CrimeLiability SpecialConditions');

      result.crimeTypeOfPolicy = getText(doc, 'Coverages Acord24CrimeLiability TypeOfPolicy');

      const cr1 = parseLimitEl(doc, 'Limits Acord24CrimeLiability Other1');
      result.crimeCov1 = cr1.checked; result.crimeCov1Lim = cr1.value; result.crimeCov1Desc = cr1.desc;
      const cr2 = parseLimitEl(doc, 'Limits Acord24CrimeLiability Other2');
      result.crimeCov2 = cr2.checked; result.crimeCov2Lim = cr2.value; result.crimeCov2Desc = cr2.desc;
      const cr3 = parseLimitEl(doc, 'Limits Acord24CrimeLiability Other3');
      result.crimeCov3 = cr3.checked; result.crimeCov3Lim = cr3.value; result.crimeCov3Desc = cr3.desc;

    } else if (hasCov('Acord24BoilerLiability')) {
      result.boilerChecked = yn(getText(doc, 'Coverages Acord24BoilerLiability Boiler'));
      const xmlLtr = getText(doc, 'Coverages Acord24BoilerLiability InsurerLtr');
      const letter = xmlLtr || assignInsurer(cov.policyNumber, cov.naiCid);
      setSlotId(letter, cov.naiCid);
      result.boilerInsurerLtr  = letter;
      result.boilerPolicyNumber = cov.policyNumber || '';
      result.boilerEffDate      = parseStoredDate(cov.effectiveDate);
      result.boilerExpDate      = parseStoredDate(cov.expirationDate);

      const boA = getText(doc, 'Acord24BoilerLiability InsurerA');
      const boB = getText(doc, 'Acord24BoilerLiability InsurerB');
      const boC = getText(doc, 'Acord24BoilerLiability InsurerC');
      const boD = getText(doc, 'Acord24BoilerLiability InsurerD');
      const boE = getText(doc, 'Acord24BoilerLiability InsurerE');
      const boF = getText(doc, 'Acord24BoilerLiability InsurerF');
      if (boA && !result.insurerA) result.insurerA = boA;
      if (boB && !result.insurerB) result.insurerB = boB;
      if (boC && !result.insurerC) result.insurerC = boC;
      if (boD && !result.insurerD) result.insurerD = boD;
      if (boE && !result.insurerE) result.insurerE = boE;
      if (boF && !result.insurerF) result.insurerF = boF;
      const boAId = getText(doc, 'Acord24BoilerLiability InsurerAId');
      const boBId = getText(doc, 'Acord24BoilerLiability InsurerBId');
      const boCId = getText(doc, 'Acord24BoilerLiability InsurerCId');
      const boDId = getText(doc, 'Acord24BoilerLiability InsurerDId');
      const boEId = getText(doc, 'Acord24BoilerLiability InsurerEId');
      const boFId = getText(doc, 'Acord24BoilerLiability InsurerFId');
      if (boAId && !result.insurerAId) result.insurerAId = boAId;
      if (boBId && !result.insurerBId) result.insurerBId = boBId;
      if (boCId && !result.insurerCId) result.insurerCId = boCId;
      if (boDId && !result.insurerDId) result.insurerDId = boDId;
      if (boEId && !result.insurerEId) result.insurerEId = boEId;
      if (boFId && !result.insurerFId) result.insurerFId = boFId;

      if (!result.locationProperty)
        result.locationProperty = getText(doc, 'Coverages Acord24BoilerLiability LocationOfPremises');
      if (!result.descriptionOfProperty)
        result.descriptionOfProperty = getText(doc, 'Coverages Acord24BoilerLiability DescriptionOfProperty');
      if (!result.specialConditions)
        result.specialConditions = getText(doc, 'Coverages Acord24BoilerLiability SpecialConditions');

      const bo1 = parseLimitEl(doc, 'Limits Acord24BoilerLiability Other1');
      result.boilerCov1 = bo1.checked; result.boilerCov1Lim = bo1.value; result.boilerCov1Desc = bo1.desc;
      const bo2 = parseLimitEl(doc, 'Limits Acord24BoilerLiability Other2');
      result.boilerCov2 = bo2.checked; result.boilerCov2Lim = bo2.value; result.boilerCov2Desc = bo2.desc;

    } else if (hasCov('Acord24OtherLiability')) {
      const xmlLtr = getText(doc, 'Coverages Acord24OtherLiability InsurerLtr');
      const letter = xmlLtr || assignInsurer(cov.policyNumber, cov.naiCid);
      setSlotId(letter, cov.naiCid);
      result.otherInsurerLtr  = letter;
      result.otherPolicyNumber = cov.policyNumber || '';
      result.otherEffDate      = parseStoredDate(cov.effectiveDate);
      result.otherExpDate      = parseStoredDate(cov.expirationDate);
      result.otherType = getText(doc, 'Coverages Acord24OtherLiability Other');

      const otA = getText(doc, 'Acord24OtherLiability InsurerA');
      const otB = getText(doc, 'Acord24OtherLiability InsurerB');
      const otC = getText(doc, 'Acord24OtherLiability InsurerC');
      const otD = getText(doc, 'Acord24OtherLiability InsurerD');
      const otE = getText(doc, 'Acord24OtherLiability InsurerE');
      const otF = getText(doc, 'Acord24OtherLiability InsurerF');
      if (otA && !result.insurerA) result.insurerA = otA;
      if (otB && !result.insurerB) result.insurerB = otB;
      if (otC && !result.insurerC) result.insurerC = otC;
      if (otD && !result.insurerD) result.insurerD = otD;
      if (otE && !result.insurerE) result.insurerE = otE;
      if (otF && !result.insurerF) result.insurerF = otF;
      const otAId = getText(doc, 'Acord24OtherLiability InsurerAId');
      const otBId = getText(doc, 'Acord24OtherLiability InsurerBId');
      const otCId = getText(doc, 'Acord24OtherLiability InsurerCId');
      const otDId = getText(doc, 'Acord24OtherLiability InsurerDId');
      const otEId = getText(doc, 'Acord24OtherLiability InsurerEId');
      const otFId = getText(doc, 'Acord24OtherLiability InsurerFId');
      if (otAId && !result.insurerAId) result.insurerAId = otAId;
      if (otBId && !result.insurerBId) result.insurerBId = otBId;
      if (otCId && !result.insurerCId) result.insurerCId = otCId;
      if (otDId && !result.insurerDId) result.insurerDId = otDId;
      if (otEId && !result.insurerEId) result.insurerEId = otEId;
      if (otFId && !result.insurerFId) result.insurerFId = otFId;

      if (!result.locationProperty)
        result.locationProperty = getText(doc, 'Coverages Acord24OtherLiability LocationOfPremises');
      if (!result.descriptionOfProperty)
        result.descriptionOfProperty = getText(doc, 'Coverages Acord24OtherLiability DescriptionOfProperty');
      if (!result.specialConditions)
        result.specialConditions = getText(doc, 'Coverages Acord24OtherLiability SpecialConditions');

      const ot1 = parseLimitEl(doc, 'Limits Acord24OtherLiability Other1');
      result.otherCov1 = ot1.checked; result.otherCov1Lim = ot1.value; result.otherCov1Desc = ot1.desc;
      const ot2 = parseLimitEl(doc, 'Limits Acord24OtherLiability Other2');
      result.otherCov2 = ot2.checked; result.otherCov2Lim = ot2.value; result.otherCov2Desc = ot2.desc;

    } else if (hasCov('Acord101AdditionalRemarks')) {
      result.acord101AdditionalRemarks = getText(doc, 'Acord101AdditionalRemarks RemarksText');
    } else if (hasCov('CertificateHolderInformationCoverage')) {
      result.holderName    = getText(doc, 'CertificateHolderInformationCoverage CertificateHolder Name');
      result.holderAddress = getText(doc, 'CertificateHolderInformationCoverage CertificateHolder Address');
      result.holderCity    = getText(doc, 'CertificateHolderInformationCoverage CertificateHolder City');
      result.holderStreet  = getText(doc, 'CertificateHolderInformationCoverage CertificateHolder Street');
      result.holderState   = getText(doc, 'CertificateHolderInformationCoverage CertificateHolder State');
      result.holderZip     = getText(doc, 'CertificateHolderInformationCoverage CertificateHolder Zip');
    }
  }

  // Fallback: fill insurer slots from policy carrier names if not set from XML
  if (!result.insurerA) result.insurerA = insurerSlots['A'] || '';
  if (!result.insurerB) result.insurerB = insurerSlots['B'] || '';
  if (!result.insurerC) result.insurerC = insurerSlots['C'] || '';
  if (!result.insurerD) result.insurerD = insurerSlots['D'] || '';
  if (!result.insurerE) result.insurerE = insurerSlots['E'] || '';
  if (!result.insurerF) result.insurerF = insurerSlots['F'] || '';

  return result;
}
